import { useRef, useState } from "react";
import { createElement } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// Preserve the existing PDF document templates, but do not render any PDF during
// transaction-list/search/preview updates. Load and generate only on user request.
export default function OnDemandPDFExport({ currentRows, getRows, kind = "sale", label, companyName }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const working = useRef(false);

  async function download() {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    setError(false);
    try {
      // Evaluate records at click time, after selection and filters are current.
      const records = getRows ? getRows() : currentRows || [];
      const [{ pdf }, exports] = await Promise.all([
        import("@react-pdf/renderer"),
        ["order", "receive", "return"].includes(kind) ? import("./OrdersPDFExport.jsx") : import("./SalesPDFExport.jsx"),
      ]);
      const isOrder = ["order", "receive", "return"].includes(kind);
      const Doc = isOrder ? exports.PurchasePDFDocument : exports.TransactionPDFDocument;
      const title = isOrder ? `${exports.purchaseTitles[kind]} Records` : exports.recordTitles[kind];
      const id = records.length === 1
        ? (isOrder ? exports.purchaseSummary(records[0], kind).id : exports.transactionSummary(records[0], kind).id)
        : title;
      const name = String(id || title || "transactions").replace(/[\\/:*?"<>|]/g, "_").replace(/[^a-zA-Z0-9._ -]/g, "_");
      const pdfDocument = createElement(Doc, { data: records, kind, companyName });
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = documentCreateAnchor();
      link.href = url;
      link.download = `${name}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (failure) {
      console.error("Transaction PDF export failed:", failure);
      setError(true);
    } finally {
      working.current = false;
      setBusy(false);
    }
  }

  return <button type="button" className={kind === "order" ? "iconBtn" : "controlButtons"}
    disabled={busy} onClick={download} aria-label={label || "Export PDF"}
    title={error ? "PDF unavailable — click to retry" : "Export PDF"}>
    <PictureAsPdfIcon fontSize="large" />
    {busy ? <span>Preparing PDF…</span> : error ? <span>Retry PDF</span> : (label ? <span>{label}</span> : null)}
  </button>;
}

function documentCreateAnchor() { return document.createElement("a"); }
